import React, { useState } from 'react';
import dayjs from 'dayjs';

import { ReservationFormDialog, ReservationFormValues } from '@spazious/dollhouse-booking';
import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import {
  useCreateProposalMutation,
  useUpdateGraphMutation,
} from '@spazious/dollhouse-graphql/apollo';
import { Translate } from '@spazious/dollhouse-i18n';
import { FShallowGraph, GraphActionType } from '@spazious/dollhouse-shallow-graph';
import { useDeviceInfo, useEvents, useParams } from '@spazious/dollhouse-shared';
import { useGraph, useHistory } from '@spazious/dollhouse-store';
import { ModeTypeEnum } from '@spazious/dollhouse-types';
import { Button, ButtonProps } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

/**
 * Ensure that the graph is in the correct format
 */
export const parseGraph = (graph: FShallowGraph) => {
  const { cursor, nodes, version, name, space } = graph;
  return {
    cursor,
    nodes,
    version,
    name,
    space,
  };
};

const DATE_FORMAT = 'YYYY-MM-DD';

export const SaveButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { graph } = useGraph();
  const { setupGraphId, mode, hotelId, set: setParams } = useParams();
  const events = useEvents();
  const history = useHistory();
  const spaceId = useGraph().graph.shallow.space;

  const isMobile = useDeviceInfo((state) => state.isMobile);
  const buttonSize: ButtonProps['size'] = isMobile ? 'small' : 'medium';

  const [updateGraph] = useUpdateGraphMutation();

  const [createProposal] = useCreateProposalMutation();

  const resetHistory = useEventCallback((graph: FShallowGraph) => {
    history.reset(
      [
        {
          type: GraphActionType.Init,
          payload: graph,
        },
      ],
      CommanderActionTypesEnum.Init
    );
  });
  const handleCreateGraph = useEventCallback(async (formValues: ReservationFormValues) => {
    try {
      const { proposalName, attendees = 1, endDate, startDate, ...restFormValue } = formValues;

      if (!hotelId) {
        events.emit('error', `Missing hotel id`);
        return;
      }
      const { data } = await createProposal({
        variables: {
          input: {
            ...restFormValue,
            attendees: parseInt(attendees as any),
            endDate: dayjs(endDate).format(DATE_FORMAT),
            startDate: dayjs(startDate).format(DATE_FORMAT),
            hotelId: hotelId,
          },
          graph: {
            ...parseGraph(graph.shallow),
            name: proposalName,
            space: spaceId,
          } as any,
        },
      });
      events.emit('save', data!.createProposal.graph);
      events.emit(
        'notification',
        `Setup saved successfully with id: ${data!.createProposal.graph.id}`
      );
      if (data!.createProposal.graph) {
        resetHistory(parseGraph(data!.createProposal.graph as any));
      }

      setParams({
        setupGraphId: data?.createProposal.graph.id,
        mode: ModeTypeEnum.Edit,
      });
      setOpen(false);
    } catch (ex: unknown) {
      events.emit(
        'error',
        `Something went wrong while saving: ${(ex as Error).message}, try it later`
      );
    }
  });

  const handleSaveClick = useEventCallback(async () => {
    if (!spaceId) {
      events.emit('error', 'Missing spaceId');
      return;
    }

    switch (mode) {
      case ModeTypeEnum.Create: {
        setOpen(true);
        break;
      }
      case ModeTypeEnum.Edit: {
        if (!setupGraphId) {
          events.emit('error', 'Missing setupGraphId');
          break;
        }
        try {
          const { data } = await updateGraph({
            variables: {
              id: setupGraphId,
              graph: {
                ...parseGraph(graph.shallow),
                space: spaceId,
              } as any,
            },
          });

          events.emit('save', data!.updateGraph.id);
          events.emit('notification', `Setup saved successfully with id: ${data!.updateGraph.id}`);
          setParams({
            setupGraphId: data!.updateGraph.id,
          });
          if (data!.updateGraph) {
            resetHistory(parseGraph(data!.updateGraph as any));
          }
        } catch (ex: unknown) {
          events.emit(
            'error',
            `Something went wrong while saving: ${(ex as Error).message}, try it later`
          );
        }
        break;
      }
    }
  });

  return (
    <>
      <Button
        color="primary"
        disabled={history.index === 0}
        size={buttonSize}
        onClick={handleSaveClick}
      >
        <Translate>Save</Translate>
      </Button>
      <ReservationFormDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleCreateGraph}
      />
    </>
  );
};
